import { useId, useState } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import EditIcon from '@mui/icons-material/Edit';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import HistoryIcon from '@mui/icons-material/History';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useTranslation } from 'react-i18next';
import type { InventoryItem } from '@/types';

interface InventoryItemActionsProps {
  item: InventoryItem;
  onEdit?: (item: InventoryItem) => void;
  onAdjustStock?: (item: InventoryItem) => void;
  onLocations: (item: InventoryItem) => void;
  onTransactions: (item: InventoryItem) => void;
  onOrder?: (item: InventoryItem) => void;
}

export function InventoryItemActions({
  item, onEdit, onAdjustStock, onLocations, onTransactions, onOrder,
}: InventoryItemActionsProps) {
  const { t } = useTranslation('inventory');
  const id = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const selectAction = (action: (item: InventoryItem) => void) => {
    setAnchorEl(null);
    action(item);
  };

  return (
    <>
      <Tooltip title={t('itemActions', { name: item.name })}>
        <IconButton
          id={`${id}-button`}
          aria-label={t('itemActions', { name: item.name })}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : undefined}
          aria-controls={open ? `${id}-menu` : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <MoreHorizIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ 'aria-labelledby': `${id}-button` }}
      >
        {onEdit && (
          <MenuItem onClick={() => selectAction(onEdit)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('editItem')}</ListItemText>
          </MenuItem>
        )}
        {onAdjustStock && (
          <MenuItem onClick={() => selectAction(onAdjustStock)}>
            <ListItemIcon><SyncAltIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('editStock')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => selectAction(onLocations)}>
          <ListItemIcon><WarehouseIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('stockLocations.title')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectAction(onTransactions)}>
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('transactions')}</ListItemText>
        </MenuItem>
        {onOrder && (
          <MenuItem onClick={() => selectAction(onOrder)}>
            <ListItemIcon><ShoppingCartIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('orderStock')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
